import { Heart, Shield, Users, Stethoscope, MessageCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useSiteSetting } from "@/hooks/useSiteSetting";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import therapyFamilyImg from "@/assets/landing/therapy-family.webp";
import familyImpactImg from "@/assets/landing/family-impact.webp";

const services = [
  {
    icon: Heart,
    title: "Recuperação Familiar Sustentável",
    desc: "Nosso programa proporciona uma recuperação familiar sustentável, com acompanhamento de longo prazo e suporte emocional para toda a família.",
    image: therapyFamilyImg,
    imageAlt: "Família em terapia - recuperação sustentável",
  },
  {
    icon: Stethoscope,
    title: "Tratamento Personalizado",
    desc: "Cada paciente é único. Oferecemos planos terapêuticos individualizados para dependência química, alcoolismo e transtornos psiquiátricos.",
    image: familyImpactImg,
    imageAlt: "Apoio emocional para famílias - tratamento personalizado",
  },
];

const cards = [
  {
    icon: Shield,
    title: "Ambiente Seguro e Acolhedor",
    desc: "Estrutura completa com supervisão 24 horas, equipe qualificada e protocolos de segurança para garantir o bem-estar do paciente.",
  },
  {
    icon: Users,
    title: "Acompanhamento Familiar",
    desc: "Terapia familiar integrada ao tratamento, fortalecendo os laços e preparando a família para a reintegração do paciente.",
  },
];

function ServiceRow({ s, i }: { s: typeof services[0]; i: number }) {
  const ref = useScrollReveal<HTMLDivElement>();
  const whatsappPhone = useSiteSetting("whatsapp_phone", "5562994377238");

  return (
    <div
      ref={ref}
      className={`scroll-reveal flex flex-col ${i % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"} gap-6 items-center`}
    >
      <div className="md:w-1/2">
        <div className="rounded-xl overflow-hidden shadow-lg border border-border/50">
          <img
            src={s.image}
            alt={s.imageAlt}
            className="w-full h-64 sm:h-80 object-cover"
            loading="lazy"
            decoding="async"
            width={640}
            height={320}
          />
        </div>
      </div>
      <div className="md:w-1/2 space-y-4">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
          <s.icon className="h-6 w-6 text-primary" />
        </div>
        <h3 className="text-xl font-bold text-foreground">{s.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
        <Button className="gap-2" asChild>
          <a
            href={`https://wa.me/${whatsappPhone}?text=Olá! Gostaria de saber mais sobre ${s.title}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <MessageCircle className="h-4 w-4" /> Atendimento WhatsApp
          </a>
        </Button>
      </div>
    </div>
  );
}

export default function LandingServices() {
  const headerRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="max-w-5xl mx-auto px-4 py-16">
      <div ref={headerRef} className="scroll-reveal grid md:grid-cols-2 gap-6 mb-12">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
            Ajuda Profissional para Lidar com a Dependência
          </h2>
        </div>
        <div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Oferecemos soluções completas, com foco no bem-estar, na eficiência e na excelência
            no atendimento. Cada detalhe foi pensado para entregar uma experiência única, segura
            e confiável do início ao fim.
          </p>
        </div>
      </div>

      <div className="space-y-12 mb-12">
        {services.map((s, i) => (
          <ServiceRow key={s.title} s={s} i={i} />
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {cards.map((s) => (
          <Card
            key={s.title}
            className="border-border/50 bg-card/80 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
          >
            <CardContent className="p-5 flex flex-col items-center text-center gap-3">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <s.icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-sm font-semibold text-foreground">{s.title}</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">{s.desc}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}
